import {
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SkeletonText,
  Text,
  VStack,
  ButtonGroup,
  Button,
  Heading,
  useDisclosure,
  Input,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { useEffect, useRef, useState, useMemo } from "react";
import { EditorContent } from "@tiptap/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import {
  FirstPageIcon,
  LastPageIcon,
  LeftIcon,
  RightIcon,
} from "../../../../constants/commonIcons";
import { useEditorWithExtensions } from "../utils/useEditorWithExtensions";
import useToast from "../../../../utilities/useToast";
import { formatDateSinceNow } from "../../../../utilities/dateUtils";

type Props = {
  sectionName: string;
  sectionID: string;
  isOpen: boolean;
  canEdit: boolean;
  onClose: () => void;
};
type ConfirmProps = {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  loading: boolean;
  sectionName: string;
  versionNumber: number;
};
function ConfirmRevertModal({
  isOpen,
  onClose,
  onUpdate,
  loading,
  sectionName,
  versionNumber,
}: ConfirmProps) {
  const initialFocusRef = useRef(null);
  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <chakra-scope>
        <ModalContent>
          <ModalHeader>Revert section content?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to revert the manual section &quot;
              {sectionName}&quot; back to version {versionNumber + 1}?
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                ref={initialFocusRef}
                onClick={onClose}
                colorScheme="gray">
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="red"
                onClick={() => onUpdate()}>
                Revert
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </chakra-scope>
    </Modal>
  );
}
export function ViewHistoryModal({
  sectionName,
  sectionID,
  isOpen,
  canEdit,
  onClose,
}: Props) {
  const historyQuery = useApiQuery("manual/getManualSectionContentHistory", {
    manualSectionID: sectionID || "",
  });
  const revertSectionMutation = useApiMutation("manual/updateSection");

  const { isLoading, isSuccess } = historyQuery;
  const sections = useMemo(() => {
    return historyQuery.data?.sections || [];
  }, [historyQuery.data]);
  const initialFocusRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(
    sections?.length > 0 ? sections.length - 1 : 0,
  );
  useEffect(() => {
    setCurrentIndex(sections?.length > 0 ? sections.length - 1 : 0);
  }, [sections]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      setInputValue((currentIndex + 1).toString());
    }
  }, [isOpen, currentIndex]);

  const toast = useToast();
  const confirmModal = useDisclosure();
  const queryClient = useQueryClient();
  const currentSection = sections?.[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setInputValue(currentIndex.toString());
  };
  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, sections.length - 1));
    setInputValue((currentIndex + 2).toString());
  };
  const handleFirst = () => {
    setCurrentIndex(0);
    setInputValue("1");
  };
  const handleLast = () => {
    setCurrentIndex(sections.length - 1);
    setInputValue(sections.length.toString());
  };

  const editor = useEditorWithExtensions({
    content: "",
    placeholder: "",
    limit: 20000,
    editable: false,
  });

  useEffect(() => {
    if (currentSection?.content) {
      try {
        const parsedContent = JSON.parse(currentSection.content);
        editor?.commands.setContent(parsedContent);
      } catch (error) {
        console.error("Error parsing content:", error);
      }
    }
  }, [currentSection, editor]);
  const handlePageNavigation = () => {
    const page = parseInt(inputValue, 10);
    if (!Number.isNaN(page) && page > 0 && page <= sections.length) {
      setCurrentIndex(page - 1);
    } else {
      // Reset to current page if invalid input
      setInputValue((currentIndex + 1).toString());
      if (
        inputValue.trim() !== "" &&
        inputValue !== (currentIndex + 1).toString()
      ) {
        toast.displayToast({
          title: "Invalid Page Number",
          status: "error",
        });
      }
    }
  };
  const handleRevertSection = async () => {
    const newJson = currentSection?.content;
    const newText = currentSection?.contentRawPreview;
    const newTitle = sectionName;

    if (!sectionID) {
      return false;
    }

    const newSection = {
      manualSectionID: sectionID,
      title: newTitle,
      content: {
        jsonString: newJson || "",
        rawPreview: (newText || "").slice(0, 255),
      },
    };

    try {
      await revertSectionMutation.mutateAsync({ section: newSection });
    } catch (e) {
      console.error("Failed to update section", e);
      toast.displayToast({
        title: "Failed to update section",
        status: "error",
      });
      return false;
    }

    toast.displayToast({
      title: "Updated section",
      status: "success",
    });
    confirmModal.onClose();
    queryClient.invalidateQueries({ queryKey: ["manual"] });
    onClose();
    return true;
  };

  return (
    <Modal
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onClose}
      size="4xl">
      <ModalOverlay />
      <chakra-scope>
        <ModalContent>
          <ModalHeader>
            Section History - {sectionName} {"  "}
            {currentIndex + 1 === sections.length && (
              <Badge colorScheme="gray">Current Version</Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          {isLoading ? (
            <ModalBody>
              <SkeletonText
                my="2"
                noOfLines={3}
                spacing="2"
                skeletonHeight="3"
              />
            </ModalBody>
          ) : !isSuccess || !sections?.length ? (
            <ModalBody m={4}>
              <Text color="gray.600">
                No history available for section: &quot;{sectionName}&quot;.
              </Text>
            </ModalBody>
          ) : (
            <>
              <ModalBody>
               {currentSection && <VStack align="left" gap="2">
                  <Heading size="sm">
                    Version {currentIndex + 1} - {" "}
                    <Box textTransform="capitalize" as="span">{formatDateSinceNow(new Date(currentSection.createTs))}</Box>
                  </Heading>
                  <Text fontSize="xs" color="gray.600">
                    {new Date(currentSection.createTs).toLocaleString()}
                  </Text>
                  <HStack>
                    <Text fontSize="xs" color="gray.600">
                      Modified by {currentSection?.createUserName}
                    </Text>
                  </HStack>
                  <Box overflowY="auto" maxH="400px">
                    <EditorContent readOnly editor={editor} />
                  </Box>
                </VStack>}
              </ModalBody>
              <ModalFooter>
                <HStack w="full">
                  {currentIndex + 1 !== sections.length && (
                    <Tooltip
                      label={
                        !canEdit
                          ? "You don't have permission to edit this"
                          : undefined
                      }>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        isDisabled={!canEdit}
                        isLoading={revertSectionMutation.isPending}
                        onClick={() => confirmModal.onOpen()}>
                        Revert
                      </Button>
                    </Tooltip>
                  )}
                  <HStack w="full" justifyContent="center">
                    <ButtonGroup>
                      <IconButton
                        variant="ghost"
                        aria-label="First"
                        onClick={handleFirst}
                        isDisabled={currentIndex === 0}
                        icon={<FirstPageIcon />}
                      />
                      <IconButton
                        variant="ghost"
                        aria-label="Previous"
                        icon={<LeftIcon />}
                        onClick={handlePrevious}
                        isDisabled={currentIndex === 0}
                      />
                    </ButtonGroup>
                    <HStack>
                      <HStack>
                        <Text>Page</Text>
                        <Input
                          size="sm"
                          width="50px"
                          textAlign="center"
                          placeholder={`${currentIndex + 1}`}
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handlePageNavigation();
                            }
                          }}
                          onBlur={handlePageNavigation}
                        />
                        <Text>of {sections.length}</Text>
                      </HStack>
                    </HStack>
                    <ButtonGroup>
                      <IconButton
                        variant="ghost"
                        aria-label="Next"
                        icon={<RightIcon />}
                        onClick={handleNext}
                        isDisabled={currentIndex === sections.length - 1}
                      />
                      <IconButton
                        variant="ghost"
                        aria-label="Last"
                        onClick={handleLast}
                        isDisabled={currentIndex === sections.length - 1}
                        icon={<LastPageIcon />}
                      />
                    </ButtonGroup>
                  </HStack>
                </HStack>
                <ConfirmRevertModal
                  isOpen={confirmModal.isOpen}
                  onClose={confirmModal.onClose}
                  onUpdate={handleRevertSection}
                  sectionName={sectionName}
                  versionNumber={currentIndex}
                  loading={revertSectionMutation.isPending}
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </chakra-scope>
    </Modal>
  );
}
